import { ExternalLink, FileText, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useUpdateTaskMutation } from '@/services/api';
import { Blob } from '@/services/types/blob-types';
import { Task } from '@/services/types/client-intake-types';

import { DocumentSelectionModal } from './DocumentSelectionModal';

interface TaskAttachmentsProps {
  task: Task;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
}

export const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  task,
  onDeleteAttachment,
}) => {
  const [attachmentToDelete, setAttachmentToDelete] =
    React.useState<Blob | null>(null);
  const [documentSelectionOpen, setDocumentSelectionOpen] =
    React.useState(false);
  const [updateTask] = useUpdateTaskMutation();

  // Use the upload manager hook for file uploads
  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId: task.matter,
    refetch: () => {
      // The modal will handle refetching blobs
    },
  });

  const handleSelectDocuments = async (blobIds: string[]) => {
    try {
      await updateTask({
        matterId: task.matter,
        taskId: task.id,
        task: {
          attachments: Array.from(blobIds),
        },
      }).unwrap();
      toast.success(`${blobIds.length} attachment(s) saved`);
    } catch (error) {
      console.error('Failed to update attachments:', error);
      toast.error('Failed to save attachments. Please try again.');
    }
  };

  const handleDeleteAttachment = async () => {
    if (attachmentToDelete) {
      await onDeleteAttachment(attachmentToDelete.id);
      setAttachmentToDelete(null);
    }
  };

  return (
    <div className="space-y-2">
      {/* Attachments List */}
      {!task.attachments || task.attachments.length === 0 ? (
        <div className="text-center py-2 text-slate-500">
          <div className="text-xs font-medium">No attachments yet</div>
        </div>
      ) : (
        <div className="space-y-2">
          {task.attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="group flex items-center justify-between px-2 py-1.5 border border-slate-200 rounded-md hover:border-slate-300 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <a
                  href={attachment.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-900 hover:text-blue-600 truncate font-semibold"
                  title={attachment.name}
                >
                  {attachment.name}
                </a>
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-slate-500 hover:text-slate-700"
                  onClick={() => window.open(attachment.download_url, '_blank')}
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-slate-500 hover:text-red-600"
                  onClick={() => setAttachmentToDelete(attachment)}
                  title="Delete attachment"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Attachment Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDocumentSelectionOpen(true)}
        className="w-full justify-center gap-1.5 border-dashed border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-700 h-7 text-xs font-semibold"
      >
        <Plus className="h-3 w-3" />
        Add Attachment
      </Button>

      {/* Document Selection Modal */}
      <DocumentSelectionModal
        open={!!documentSelectionOpen}
        onClose={() => {
          setDocumentSelectionOpen(false);
        }}
        matterId={task.matter}
        onSelectDocuments={handleSelectDocuments}
        initialSelectedBlobIds={task.attachments?.map((a) => a.id) || []}
        onUpload={handleUploadToCollection}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!attachmentToDelete}
        onOpenChange={() => setAttachmentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove &ldquo;
              {attachmentToDelete?.name}&rdquo; from this task?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAttachment}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
